/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  useEffect,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {
  LogicalPosition
} from '@ion/core/src/types/editors';
import {
  GenericObject,
  PhoneType
} from '@ion/core/src/types/entities';
import {smoothScrollByElement} from '@ion/core/src/utils/BrowserUtils';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {TertiaryIconButton} from '@ion/dls/src/components/buttons/TertiaryIconButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {PhoneNumber} from '@ion/dls/src/components/phone/PhoneNumber';
import {restrictedNumberChars} from '@ion/dls/src/components/phone/Utils';
import {SystemDeleteIcon} from '@ion/dls/src/icons/SystemDeleteIcon';
import ContactTypeChooser from './ContactTypeChooser';

export interface ContactProps {
  isCtcAdmin?: boolean;
  onChange: (contact: GenericObject) => void;
  readOnly: boolean;
  required: boolean;
  classes?: string;
  contactInfo: GenericObject;
  minContact?: number;
  bottomBorder?: boolean;
  phoneError?: boolean;
  phoneNumbers?: Array<string>;
  isPhoneNumberError?: boolean;
};

const useStyles = makeStyles(theme => ({
  container : {
    margin : '0px'
  },
  typoStyle : {
    fontSize : '14px !important',
    width    : '100%'
  },
  subHeader : {
    display : 'flex',
    width   : '100%',
    padding : '16px',
    height  : '0em'
  },
  deleteStyle : {
    top : '25px'
  },
  iconStyle : {
    margin   : '-32px',
    position : 'relative',
    top      : '55px',
    left     : '25px'
  }
}));

/**
 * @contact
 *
 * The contact Editor provides the UI which allows a user to update their default
 * contact, such as their state, country etc..
 *
 *    import {contact} from '@ion/features/src/entities/contact';
 *
 *    <contacts
 *       onChange={onChange} />
 *
 */
export const Contacts = forwardRef(({
  readOnly,
  onChange,
  contactInfo,
  phoneNumbers,
  minContact = 0,
  bottomBorder = true,
  isCtcAdmin = false,
  phoneError = false,
  isPhoneNumberError = false
}: ContactProps, ref) => {
  const classes                           = useStyles();
  const [contactsLevel, setContactsLevel] = useState(contactInfo || []);
  const omitFields                        = !readOnly;
  const isValidPhoneNumber                = phoneNumbers?.length > 3;
  const position                          = isValidPhoneNumber ? LogicalPosition.Center : LogicalPosition.End;

  useEffect(() => {
    if (contactInfo?.length > 0) {
      setContactsLevel(contactInfo);
    }
  }, [contactInfo]);

  useEffect(() => {
    if (minContact > 0 && isEmpty(contactInfo)) {
      createContact();
    }
  }, []);

  if (phoneNumbers && isPhoneNumberError) {
    smoothScrollByElement('contactEditor', position);
  }

  /**
   * create new row for contact i.e contacts
   */
  function createContact() {
    const level = Object.assign([], contactsLevel);
    const contact = {
      phoneType           : PhoneType.HOME,
      isPhoneNumError     : false,
      PhoneError          : false,
      phoneNumber         : '',
      isExtensionNumError : false,
      extension           : ''
    };
    level.push({id: contactsLevel.length + 1, phoneNumbers: contact});
    onChange(level);
    setContactsLevel(level);
  }

  /**
   * create new row for contact i.e contacts
   */
  function deleteContact({id}) {
    if (minContact !== contactsLevel?.length) {
      const level = contactsLevel?.filter((item) => Number(item.id) !== Number(id));
      setContactsLevel(level);
      onChange(level);
    }
  }

  function handleFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e?.persist();
    const {name, value, id} = e.target;
    addLevel(id, value, name);
  };

  function handleNumberFieldKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    e.persist();
    if (restrictedNumberChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Invoked when the user selects a types.
   */
  const handleContactTypes = function(type: string, {id}): void {
    addLevel(id, type, false);
  };

  const addLevel = (id, value, name) => {
    const addContact = contactsLevel.map((item) => {
      if (Number(item.id) === Number(id)) {
        name ? item.phoneNumbers= {
          ...item.phoneNumbers,
          [name] : value
        } : item.phoneNumbers= {
          ...item.phoneNumbers,
          phoneType : value
        };
      }
      return item;
    });
    onChange(addContact);
    setContactsLevel(addContact);
  };

  const onPhoneNumberChange = function(newPhoneNumber: string, id: any) {
    addLevel(id, newPhoneNumber, translate('contacts:phone.phoneNumber'));
  };

  const onExtensionNumberChange = function(e) {
    e.target.value = Math.max(0, parseInt(e.target.value)).toString().slice(0, 6);
  };

  return (
    <div id={'contactEditor'} className={classes.container}>
      <FormSection bottomBorder={bottomBorder}
        title={translate('contacts:contacts')}
        legend={!readOnly && (
          <TertiaryButton disabled={contactsLevel?.length > 3} onClick={createContact}>
            {translate('contacts:add')}
          </TertiaryButton>
        )}>
        {contactsLevel?.map((item) => {
          return (<>
            <FormItem disableMinWidth customWidth={4}>
              <ContactTypeChooser readOnly={readOnly}
                key={item.id}
                keyObj={item}
                defaultValue={item?.phoneNumbers?.phoneType?.toLocaleLowerCase()}
                onChange={handleContactTypes} />
            </FormItem>
            <FormItem disableMinWidth customWidth={4}>
              {!isCtcAdmin
                ? (<FormTextField required
                  disabled={readOnly || false}
                  fieldLabel={translate('contacts:phone.number')}
                  placeholder={translate('contacts:phone.placeholder.phone')}
                  name={translate('contacts:phone.phoneNumber')}
                  maxLength={64}
                  id={item.id}
                  onChange={handleFieldChange}
                  hasError={item.phoneNumbers.PhoneError || item.phoneNumbers.isPhoneNumError}
                  errorText={
                    item.phoneNumbers.PhoneError
                      ? translate('contacts:phone.phoneError')
                      : (item.phoneNumbers.isPhoneNumError ? translate('contacts:phone.phoneNumError') : '')}
                  defaultValue={item.phoneNumbers.phoneNumber} />)
                : (<PhoneNumber disabled={readOnly}
                  required
                  formValidation={{
                    isEmptyPhoneNumber   : item?.phoneNumbers?.PhoneError || phoneError,
                    isInvalidPhoneNumber : item?.phoneNumbers?.isPhoneNumError
                  }}
                  id={item.id}
                  onChange={onPhoneNumberChange}
                  labelText={translate('contacts:phone.number')}
                  existingPhoneNumber={item?.phoneNumbers?.phoneNumber} />)}
            </FormItem>
            <FormItem disableMinWidth customWidth={4}>
              <FormTextField disabled={readOnly || false}
                fieldLabel={translate('contacts:phone.extension')}
                placeholder={translate('contacts:phone.placeholder.extension')}
                name={translate('contacts:phone.extensionname')}
                id={item.id}
                type='number'
                onInput = {onExtensionNumberChange}
                onKeyDown={handleNumberFieldKeyDown}
                onChange={handleFieldChange}
                hasError={item.phoneNumbers.isExtensionNumError}
                errorText={translate('contacts:phone.extensionError')}
                value={item.phoneNumbers.extension} />
            </FormItem>
            {omitFields && <div className={classes.iconStyle}>
              <FormItem disableMinWidth>
                <TertiaryIconButton icon={<SystemDeleteIcon />}
                  className={classes.deleteStyle} onClick={function(e) {
                    deleteContact(item);
                  }} />
              </FormItem>
            </div>}
          </>
          );
        })}
      </FormSection>
    </div>
  );
});

export default Contacts;
